import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  DateTimePickerWithRange,
} from '@ais-ui/ui-core';
import { PiClockCountdown } from 'react-icons/pi';
import { useTimeZoneContext } from '@ais-ui/utils';
import { DateRange } from 'react-day-picker';
import { dispatchTrackTimeChange } from '../../TargetsOnMap/trackEvents';

const QUICK_PRESETS = [
  { label: '1h', hours: 1 },
  { label: '4h', hours: 4 },
  { label: '12h', hours: 12 },
  { label: '24h', hours: 24 },
  { label: '3d', hours: 72 },
  { label: '7d', hours: 168 },
] as const;

interface TrackTimeControlProps {
  layerId: string;
  currentHours?: number;
}

/**
 * Clock button with popover for selecting track time range.
 * Quick presets + full DateTimePickerWithRange for custom selection.
 * Dispatches a custom event that useShowAllTracks listens for.
 */
export const TrackTimeControl = ({
  layerId,
  currentHours = 4,
}: TrackTimeControlProps) => {
  const [open, setOpen] = useState(false);
  const [selectedHours, setSelectedHours] = useState(currentHours);
  const [customRange, setCustomRange] = useState<DateRange | undefined>();
  const [showCustom, setShowCustom] = useState(false);
  const { timeZone } = useTimeZoneContext();

  // Sync local state when prop changes externally (e.g. auto-refresh updates)
  useEffect(() => {
    setSelectedHours(currentHours);
  }, [currentHours]);

  const handlePresetSelect = useCallback(
    (hours: number) => {
      setSelectedHours(hours);
      setShowCustom(false);
      const now = new Date();
      const startTime = new Date(now.getTime() - hours * 60 * 60 * 1000);
      dispatchTrackTimeChange({ layerId, startTime, endTime: now });
      setOpen(false);
    },
    [layerId],
  );

  const handleCustomSelect = useCallback(() => {
    if (!customRange?.from || !customRange?.to) return;
    setSelectedHours(0);
    dispatchTrackTimeChange({
      layerId,
      startTime: customRange.from,
      endTime: customRange.to,
    });
    setOpen(false);
  }, [layerId, customRange]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-9 w-9 flex-shrink-0 p-0 m-0"
              onClick={(e) => e.stopPropagation()}
            >
              <PiClockCountdown className="size-5" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Track time range</TooltipContent>
      </Tooltip>
      <PopoverContent
        className="w-auto p-3"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-xs font-medium mb-2">Track duration</p>
        <div className="flex flex-wrap gap-1 mb-3">
          {QUICK_PRESETS.map((preset) => (
            <Button
              key={preset.hours}
              variant={
                !showCustom && selectedHours === preset.hours
                  ? 'default'
                  : 'outline'
              }
              size="sm"
              className="text-xs h-6 px-2"
              onClick={() => handlePresetSelect(preset.hours)}
            >
              {preset.label}
            </Button>
          ))}
          <Button
            variant={showCustom ? 'default' : 'outline'}
            size="sm"
            className="text-xs h-6 px-2"
            onClick={() => setShowCustom(true)}
          >
            Custom
          </Button>
        </div>
        {showCustom && (
          <div className="space-y-2">
            <DateTimePickerWithRange
              date={customRange}
              setDate={setCustomRange}
              timeZone={timeZone}
              dateTimeFormat="yyyy-MM-dd HH:mm:ss"
              dateLimit="past"
              showPresets={false}
              defaultToTime="now"
            />
            <Button
              size="sm"
              className="w-full text-xs h-7"
              disabled={!customRange?.from || !customRange?.to}
              onClick={handleCustomSelect}
            >
              Apply
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
